import React from 'react';
import { Link } from 'react-router-dom';
import { stores } from '../data/mockData';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AllStoresPage = () => {
  const storeLogos = {
    aliexpress: { text: 'AliExpress', color: '#e62e04', font: 'font-bold text-lg' },
    asos: { text: 'asos', color: '#2d2d2d', font: 'font-bold text-2xl lowercase' },
    ebay: { colors: ['#e53238', '#0064d2', '#f5af02', '#86b817'] },
    gogreen: { text: 'Go Green', color: '#228b22', icon: '🌲' },
    iherb: { text: 'iHerb', color: '#68a648', font: 'font-bold text-xl' },
    laroche: { text: 'LA ROCHE-POSAY', color: '#0066b2', font: 'font-bold text-sm', subtitle: 'LABORATOIRE' },
    panasonic: { text: 'Panasonic', color: '#0066b2', font: 'font-bold text-xl' },
    salamander: { text: 'SALAMANDER', color: '#ff6b00', font: 'font-bold text-sm tracking-wider' },
    vichy: { text: 'VICHY', color: '#004976', font: 'font-bold text-xl', subtitle: 'LABORATOIRES' },
    'yves-roche': { text: 'YVES ROCHER', color: '#006633', font: 'font-bold text-sm', subtitle: 'FRANCE' },
    amazon: { text: 'amazon', color: '#ff9900', font: 'font-bold text-xl', arrow: true },
    walmart: { text: 'Walmart', color: '#0071ce', font: 'font-bold text-xl', spark: true },
    target: { text: 'Target', color: '#cc0000', font: 'font-bold text-xl', bullseye: true },
    bestbuy: { text: 'Best Buy', color: '#0046be', font: 'font-bold text-lg' },
    // Fashion stores
    zara: { text: 'ZARA', color: '#000000', font: 'font-bold text-2xl tracking-[0.2em]' },
    hm: { text: 'H&M', color: '#e50010', font: 'font-bold text-2xl' },
    nike: { text: 'NIKE', color: '#000000', swoosh: true },
    nordstrom: { text: 'NORDSTROM', color: '#000000', font: 'font-medium text-sm tracking-[0.1em]' },
    shein: { text: 'SHEIN', color: '#000000', font: 'font-bold text-xl tracking-wide' },
    // Cosmetics stores
    sephora: { text: 'SEPHORA', color: '#000000', font: 'font-bold text-lg tracking-[0.08em]' },
    ulta: { text: 'ULTA', color: '#000', font: 'font-extrabold text-xl', subtitleColor: '#ff6900', subtitle: 'beauty' },
    mac: { text: 'M·A·C', color: '#000000', font: 'font-bold text-2xl' },
    'charlotte-tilbury': { text: 'Charlotte Tilbury', color: '#c9a961', font: 'text-lg italic' },
    glossier: { text: 'Glossier', color: '#000', font: 'font-medium text-xl italic' },
  };

  const renderStoreLogo = (store) => {
    const logoInfo = storeLogos[store.slug];
    if (!logoInfo) return <span className="text-lg font-bold text-gray-700">{store.name}</span>;

    // eBay with colored letters
    if (store.slug === 'ebay') {
      return (
        <span className="text-2xl font-bold">
          {'ebay'.split('').map((letter, i) => (
            <span key={i} style={{ color: logoInfo.colors[i] }}>{letter}</span>
          ))}
        </span>
      );
    }

    // Go Green with icon
    if (store.slug === 'gogreen') {
      return (
        <div className="flex flex-col items-center">
          <div className="text-[#345a83] text-2xl mb-1">🌲</div>
          <span className="text-base font-bold" style={{ color: logoInfo.color }}>Go Green</span>
        </div>
      );
    }

    // Amazon with arrow
    if (store.slug === 'amazon') {
      return (
        <div className="flex flex-col items-center">
          <span className={logoInfo.font} style={{ color: logoInfo.color }}>amazon</span>
          <div className="w-10 h-0.5 mt-0.5" style={{ 
            background: logoInfo.color,
            clipPath: 'polygon(0 0, 85% 0, 100% 50%, 85% 100%, 0 100%)'
          }}></div>
        </div>
      );
    }

    // Walmart with spark
    if (store.slug === 'walmart') {
      return (
        <div className="flex items-center gap-1">
          <span className="text-yellow-500 text-lg">✦</span>
          <span className={logoInfo.font} style={{ color: logoInfo.color }}>Walmart</span>
        </div>
      );
    }

    // Target with bullseye
    if (store.slug === 'target') {
      return (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full border-4 border-red-600 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-red-600"></div>
          </div>
          <span className={logoInfo.font} style={{ color: logoInfo.color }}>Target</span>
        </div>
      );
    }

    // Nike with swoosh
    if (store.slug === 'nike') {
      return (
        <svg viewBox="0 0 24 24" className="w-12 h-12 fill-black">
          <path d="M21 8.719L7.836 14.303C6.74 14.768 5.818 15 5.075 15c-.836 0-1.445-.295-1.819-.884-.485-.76-.273-1.982.559-3.272.494-.754 1.122-1.446 1.734-2.108-.144.234-.258.48-.322.718-.156.559-.123 1.064.129 1.503.373.65 1.108.982 2.182.982.57 0 1.223-.117 1.947-.351L21 8.719z"/>
        </svg>
      );
    }

    // Ulta with beauty subtitle
    if (store.slug === 'ulta') {
      return (
        <div className="flex flex-col items-center">
          <span className={logoInfo.font} style={{ color: logoInfo.color }}>ULTA</span>
          <span className="text-xs font-bold" style={{ color: logoInfo.subtitleColor }}>beauty</span>
        </div>
      );
    }

    // Stores with subtitle
    if (logoInfo.subtitle && store.slug !== 'ulta') {
      return (
        <div className="flex flex-col items-center">
          <span className={logoInfo.font || 'text-lg font-bold'} style={{ color: logoInfo.color }}>
            {logoInfo.text}
          </span>
          <span className="text-[8px] text-gray-500 tracking-wider">{logoInfo.subtitle}</span>
        </div>
      );
    }

    // Default text logo
    return (
      <span className={logoInfo.font || 'text-xl font-bold'} style={{ color: logoInfo.color }}>
        {logoInfo.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1">
        {/* Page Header */}
        <div className="bg-white border-b mb-6">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">All Stores</h1>
            <p className="text-gray-600 mt-2">Browse deals from your favorite stores ({stores.length} stores)</p>
          </div>
        </div>

        {/* Stores Grid */}
        <div className="max-w-7xl mx-auto px-4 pb-12">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {stores.map((store, index) => (
              <Link
                key={store.id}
                to={`/store/${store.slug}`}
                className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col items-center justify-center min-h-[120px] hover:shadow-lg hover:border-[#345a83] transition-all duration-300 group animate-fadeInUp opacity-0"
                style={{ animationDelay: `${index * 0.03}s`, animationFillMode: 'forwards' }}
              >
                {renderStoreLogo(store)}
                <span className="text-gray-500 text-xs mt-3 group-hover:text-[#345a83] transition-colors">
                  View deals
                </span>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AllStoresPage;
